import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Grid,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Fade,
  useTheme as useMuiTheme
} from '@mui/material';
import {
  Send,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Star,
  Rocket,
  Command,
  Download,
  RotateCcw,
  Zap
} from 'lucide-react';
import { toast } from 'react-toastify';
import { MESSAGE_CATEGORIES, MESSAGE_TEMPLATES, OUTGOING_MESSAGE_TYPES } from '../../services/messages/messagePayloadTemplates';
import { SYSTEM_MESSAGES, getMessagesForStatus, isMessageAvailableForStatus } from '../../services/messages/statusMessageMatrix';
import LoanLookup from '../../components/messages/LoanLookup';
import { postRequest, getRequest } from '../../ApiFunction';
import { preparePayloadForBackend } from '../../utils/xmlParser';
import API from '../../Api';

// Flatten all messages for the dropdown
const ALL_MESSAGES = MESSAGE_CATEGORIES.flatMap(cat =>
  cat.messages.map(msg => ({
    ...msg,
    category: cat.name,
    categoryColor: cat.color
  }))
);

// Keep in sync with SENSITIVE_MANUAL_TRIGGER_MESSAGE_TYPES in backend loanConstants.js
const SENSITIVE_MESSAGE_TYPES = [
  'LOAN_DISBURSEMENT_NOTIFICATION',
  'LOAN_DISBURSEMENT_FAILURE_NOTIFICATION',
  'TAKEOVER_DISBURSEMENT_NOTIFICATION',
  'LOAN_LIQUIDATION_NOTIFICATION',
  'FULL_LOAN_REPAYMENT_NOTIFICATION',
  'PARTIAL_LOAN_REPAYMENT_NOTIFICATION',
  'PAYMENT_ACKNOWLEDGMENT_NOTIFICATION'
];

const getStoredPermissions = () => {
  try {
    return JSON.parse(localStorage.getItem('permissions') || '[]');
  } catch {
    return [];
  }
};

const ManualMessageTriggerV3 = () => {
    const resolveManualMessageEndpoint = (messageType) => (
      messageType === 'LOAN_STATUS_REQUEST'
        ? API.MANUAL_LOAN_STATUS_REQUEST
        : API.MANUAL_OUTGOING_MESSAGE
    );

  // Theme
  const muiTheme = useMuiTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  
  // Theme-aware colors
  const colors = {
    bgPrimary: isDark ? '#0F172A' : '#f5f7fa',
    bgSecondary: isDark ? '#1E293B' : '#ffffff',
    bgTertiary: isDark ? '#334155' : '#f1f5f9',
    border: isDark ? '#334155' : '#e2e8f0',
    textPrimary: isDark ? '#E2E8F0' : '#1a1a2e',
    textSecondary: isDark ? '#94A3B8' : '#64748b',
    textMuted: isDark ? '#64748B' : '#94a3b8',
  };
  
  // State
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [selectedMessageType, setSelectedMessageType] = useState('');
  const [payload, setPayload] = useState('');
  const [payloadTab, setPayloadTab] = useState(0); // 0: JSON, 1: Visual
  const [loading, setLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState('');
  const [validationError, setValidationError] = useState('');
  const [response, setResponse] = useState(null);
  const [responseTime, setResponseTime] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [messageHistory, setMessageHistory] = useState(() => {
    const saved = localStorage.getItem('essMessageHistory');
    return saved ? JSON.parse(saved) : [];
  });

  // Keyboard shortcut for command bar
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        document.getElementById('command-search')?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handle command bar search (search messages or use as loan ID)
  const handleCommandSearch = async () => {
    if (!searchQuery.trim()) {
      toast.warning('Please enter a search term');
      return;
    }
    
    // Check if it looks like a loan ID (starts with ESS or is alphanumeric MongoDB ID)
    const searchTerm = searchQuery.trim();
    if (searchTerm.startsWith('ESS') || /^[a-f0-9]{24}$/i.test(searchTerm)) {
      // Search for loan
      try {
        toast.info('Searching for loan...');
        const response = await getRequest(`${API.ALL_EMPLOYEES_LOAN}`);
        const { success, data, message } = response.data;

        if (!success) {
          toast.error(message || 'Failed to fetch loans');
          return;
        }

        const loans = data?.loans || [];
        const foundLoan = loans.find(
          loan => 
            loan.essApplicationNumber === searchTerm ||
            loan.essApplicationNumber?.toLowerCase() === searchTerm.toLowerCase() ||
            loan._id === searchTerm ||
            loan.Id?.toString() === searchTerm
        );

        if (foundLoan) {
          setSelectedLoan(foundLoan);
          toast.success(`Loan found: ${foundLoan.essApplicationNumber || foundLoan._id}`);
        } else {
          toast.error(`No loan found with ID: ${searchTerm}`);
        }
      } catch (error) {
        console.error('Error searching for loan:', error);
        toast.error('Error searching for loan');
      }
    } else {
      // Filter messages - already handled by filteredMessages memo
      const matchCount = filteredMessages.length;
      if (matchCount === 0) {
        toast.info('No messages match your search');
      } else if (matchCount < ALL_MESSAGES.length) {
        toast.success(`Found ${matchCount} matching message${matchCount > 1 ? 's' : ''}`);
      }
    }
  };

  // Get suggested messages based on loan status
  const suggestedMessages = useMemo(() => {
    if (!selectedLoan?.status) return [];
    const available = getMessagesForStatus(selectedLoan.status);
    return available.slice(0, 4).map(type => ALL_MESSAGES.find(m => m.type === type)).filter(Boolean);
  }, [selectedLoan]);

  // Filter messages by search
  const filteredMessages = useMemo(() => {
    if (!searchQuery.trim()) return ALL_MESSAGES;
    const query = searchQuery.toLowerCase();
    return ALL_MESSAGES.filter(msg => 
      msg.displayName.toLowerCase().includes(query) ||
      msg.type.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Process template with loan data - runs BEFORE processTemplate to preserve loan data
  // This replaces ESS{{RANDOM}} and LOAN{{RANDOM}} with actual loan values
  const processTemplateWithLoanData = (rawTemplate, loanData) => {
    // First: Apply loan data replacements to raw template string BEFORE random ID generation
    let templateStr = JSON.stringify(rawTemplate);
    
    if (loanData) {
      // Replace ESS{{RANDOM}} with actual application number
      if (loanData.essApplicationNumber) {
        templateStr = templateStr.replace(/ESS\{\{RANDOM\}\}/g, loanData.essApplicationNumber);
      }
      
      // Replace LOAN{{RANDOM}} with actual loan number
      // Priority: essLoanNumberAlias > essApplicationNumber (as-is) > _id
      // Note: If essApplicationNumber is used, we replace the entire "LOAN{{RANDOM}}" pattern
      // with just the essApplicationNumber (e.g., ESS1767086264427) since that's the actual loan identifier
      if (loanData.essLoanNumberAlias) {
        templateStr = templateStr.replace(/LOAN\{\{RANDOM\}\}/g, loanData.essLoanNumberAlias);
      } else if (loanData.essApplicationNumber) {
        // Use essApplicationNumber directly as the loan number (it's the primary identifier)
        templateStr = templateStr.replace(/LOAN\{\{RANDOM\}\}/g, loanData.essApplicationNumber);
      } else if (loanData._id) {
        templateStr = templateStr.replace(/LOAN\{\{RANDOM\}\}/g, loanData._id.toString());
      }
      
      // Replace {{CHECKNUMBER}} with actual check number
      if (loanData.essCheckNumber) {
        templateStr = templateStr.replace(/\{\{CHECKNUMBER\}\}/g, loanData.essCheckNumber);
      }
      // Replace amounts
      if (loanData.requestedAmount) {
        templateStr = templateStr.replace(/5000\.00/g, loanData.requestedAmount.toString());
        templateStr = templateStr.replace(/\{\{AMOUNT\}\}/g, loanData.requestedAmount.toString());
      }
    }
    
    // Second: Apply random replacements AFTER loan data (for any remaining placeholders)
    const now = new Date();
    const randomId = () => Math.floor(Math.random() * 100000);
    // ESS requires full ISO dateTime format, not just date
    const dateTimeStr = now.toISOString(); // Full ISO: 2026-01-01T12:00:00.000Z
    const nowStr = now.toISOString();
    
    templateStr = templateStr
      .replace(/\{\{RANDOM\}\}/g, () => randomId()) // Use function to generate unique IDs
      .replace(/\{\{NOW\}\}/g, nowStr)
      .replace(/\{\{DATE\}\}/g, dateTimeStr); // Use full dateTime format for ESS
    
    return JSON.parse(templateStr);
  };

  // Handle message type selection
  const handleMessageTypeChange = (e) => {
    const type = e.target.value;
    setSelectedMessageType(type);
    setResponse(null);
    setValidationError('');
    
    // Look for template in MESSAGE_TEMPLATES first, then OUTGOING_MESSAGE_TYPES
    const template = MESSAGE_TEMPLATES[type] || OUTGOING_MESSAGE_TYPES[type];
    if (template && template.template) {
      // processTemplateWithLoanData handles loan data replacement BEFORE random ID generation
      const processedPayload = processTemplateWithLoanData(template.template, selectedLoan);
      setPayload(JSON.stringify(processedPayload, null, 2));
    } else {
      // Fallback - create a basic payload structure
      const fallbackPayload = {
        Sender: 'ZE DONE',
        Receiver: 'ESS_UTUMISHI',
        FSPCode: 'FL8090',
        MessageType: type,
        MessageDetails: `<ApplicationNumber>${selectedLoan?.essApplicationNumber || 'ESS' + Date.now()}</ApplicationNumber>`
      };
      setPayload(JSON.stringify(fallbackPayload, null, 2));
    }
  };

  // Handle suggested message click
  const handleSuggestedClick = (msg) => {
    setSelectedMessageType(msg.type);
    setResponse(null);
    setValidationError('');
    
    // Look for template in MESSAGE_TEMPLATES first, then OUTGOING_MESSAGE_TYPES
    const template = MESSAGE_TEMPLATES[msg.type] || OUTGOING_MESSAGE_TYPES[msg.type];
    if (template && template.template) {
      // processTemplateWithLoanData handles loan data replacement BEFORE random ID generation
      const processedPayload = processTemplateWithLoanData(template.template, selectedLoan);
      setPayload(JSON.stringify(processedPayload, null, 2));
    } else {
      // Fallback - create a basic payload structure
      const fallbackPayload = {
        Sender: 'ZE DONE',
        Receiver: 'ESS_UTUMISHI',
        FSPCode: 'FL8090',
        MessageType: msg.type,
        MessageDetails: `<ApplicationNumber>${selectedLoan?.essApplicationNumber || 'ESS' + Date.now()}</ApplicationNumber>`
      };
      setPayload(JSON.stringify(fallbackPayload, null, 2));
      toast.info('Using fallback template - please update MessageDetails');
    }
  };

  // Handlers
  const handleLoanSelected = (loanData) => {
    setSelectedLoan(loanData);
    toast.success(`Loan ${loanData.essApplicationNumber || loanData._id} loaded`);
    
    // Re-process current payload with new loan data
    if (selectedMessageType) {
      const template = MESSAGE_TEMPLATES[selectedMessageType] || OUTGOING_MESSAGE_TYPES[selectedMessageType];
      if (template && template.template) {
        // processTemplateWithLoanData handles loan data replacement BEFORE random ID generation
        const processedPayload = processTemplateWithLoanData(template.template, loanData);
        setPayload(JSON.stringify(processedPayload, null, 2));
      }
    }
  };

  const clearLoan = () => {
    setSelectedLoan(null);
    toast.info('Loan context cleared');
  };

  const permissions = useMemo(() => getStoredPermissions(), []);
  const canTrigger = permissions.includes('messages:trigger');
  const canTriggerSensitive = permissions.includes('messages:trigger_sensitive');

  const hasPermissionForMessage = (messageType) => {
    if (!canTrigger) return false;
    if (SENSITIVE_MESSAGE_TYPES.includes(messageType)) return canTriggerSensitive;
    return true;
  };

  const isMessageEnabled = (messageType) => {
    if (!hasPermissionForMessage(messageType)) return false;
    if (!selectedLoan) return true;
    if (SYSTEM_MESSAGES.includes(messageType)) return true;
    return isMessageAvailableForStatus(selectedLoan.status, messageType);
  };

  const validateJSON = () => {
    try {
      JSON.parse(payload);
      setValidationError('');
      toast.success('✓ JSON is valid');
      return true;
    } catch (e) {
      setValidationError(`Invalid JSON: ${e.message}`);
      toast.error('Invalid JSON format');
      return false;
    }
  };

  const handlePayloadChange = (e) => {
    setPayload(e.target.value);
    setValidationError('');
  };

  const handleResetPayload = () => {
    if (!selectedMessageType) return;
    const template = MESSAGE_TEMPLATES[selectedMessageType] || OUTGOING_MESSAGE_TYPES[selectedMessageType];
    if (template && template.template) {
      // processTemplateWithLoanData handles loan data replacement BEFORE random ID generation
      const processedPayload = processTemplateWithLoanData(template.template, selectedLoan);
      setPayload(JSON.stringify(processedPayload, null, 2));
      setValidationError('');
      toast.info('Payload reset');
    } else {
      // Fallback
      const fallbackPayload = {
        Sender: 'ZE DONE',
        Receiver: 'ESS_UTUMISHI',
        FSPCode: 'FL8090',
        MessageType: selectedMessageType,
        MessageDetails: `<ApplicationNumber>${selectedLoan?.essApplicationNumber || 'ESS' + Date.now()}</ApplicationNumber>`
      };
      setPayload(JSON.stringify(fallbackPayload, null, 2));
      setValidationError('');
      toast.info('Payload reset to default');
    }
  };

  const handleSend = async () => {
    if (!selectedMessageType) {
      toast.error('Please select a message type');
      return;
    }

    if (!hasPermissionForMessage(selectedMessageType)) {
      toast.error(
        SENSITIVE_MESSAGE_TYPES.includes(selectedMessageType)
          ? 'You do not have permission to trigger this message type (requires elevated access)'
          : 'You do not have permission to trigger messages'
      );
      return;
    }

    if (!validateJSON()) return;

    // Look for template in both MESSAGE_TEMPLATES and OUTGOING_MESSAGE_TYPES
    const template = MESSAGE_TEMPLATES[selectedMessageType] || OUTGOING_MESSAGE_TYPES[selectedMessageType];
    
    if (!template) {
      toast.error(`No template found for message type: ${selectedMessageType}`);
      return;
    }
    
    const startTime = Date.now();
    
    try {
      setLoading(true);
      setLoadingStage('Preparing message...');
      const parsedPayload = JSON.parse(payload);

      setLoadingStage('Sending to ESS...');
      const preparedPayload = preparePayloadForBackend(parsedPayload);
      
      const endpoint = resolveManualMessageEndpoint(selectedMessageType);
      const result = await postRequest(endpoint, preparedPayload);
      const elapsed = Date.now() - startTime;
      setResponseTime(elapsed);

      const { success, data, message, sent, essResponse } = result.data;

      if (success) {
        const responseData = (sent || essResponse) ? { sent, essResponse } : data;
        
        // Extract response code from ESS XML response
        let responseCode = null;
        
        // Helper function to parse response code from XML string
        const extractResponseCodeFromXml = (xmlString) => {
          if (!xmlString || typeof xmlString !== 'string') return null;
          
          // First try regex extraction (most reliable for escaped/nested XML)
          const regexMatch = xmlString.match(/<ResponseCode>(\d+)<\/ResponseCode>/);
          if (regexMatch) {
            return regexMatch[1];
          }
          
          // Fallback: try DOM parsing
          try {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
            const parseError = xmlDoc.querySelector('parsererror');
            if (!parseError) {
              const code = xmlDoc.querySelector('ResponseCode')?.textContent;
              if (code) {
                return code;
              }
            }
          } catch (e) {
            // DOMParser failed, continue
          }
          
          return null;
        };
        
        // Try to extract from essResponse (raw XML string from Utumishi)
        if (typeof essResponse === 'string') {
          responseCode = extractResponseCodeFromXml(essResponse);
        } else if (essResponse?.ResponseDetails?.ResponseCode) {
          responseCode = essResponse.ResponseDetails.ResponseCode;
        }
        
        // Fallback: try sent.responseDetails
        if (!responseCode && sent?.responseDetails?.ResponseCode) {
          responseCode = sent.responseDetails.ResponseCode;
        }
        
        // Fallback: try parsing sent as XML if it's a string
        if (!responseCode && typeof sent === 'string') {
          responseCode = extractResponseCodeFromXml(sent);
        }
        
        setResponse({
          success: true,
          code: responseCode || 'N/A',
          data: responseData,
          message: message || 'Message sent successfully'
        });
        
        toast.success('Message sent successfully!');
        
        // Add to history
        const newHistory = [{
          id: Date.now(),
          type: selectedMessageType,
          displayName: template.displayName,
          timestamp: new Date().toISOString(),
          status: 'success',
          responseCode
        }, ...messageHistory.slice(0, 19)];
        setMessageHistory(newHistory);
        localStorage.setItem('essMessageHistory', JSON.stringify(newHistory));
      } else {
        setResponse({
          success: false,
          code: 'ERROR',
          message: message || 'Failed to send message'
        });
        toast.error(message || 'Failed to send message');
        
        // Add to history
        const newHistory = [{
          id: Date.now(),
          type: selectedMessageType,
          displayName: template.displayName,
          timestamp: new Date().toISOString(),
          status: 'failed'
        }, ...messageHistory.slice(0, 19)];
        setMessageHistory(newHistory);
        localStorage.setItem('essMessageHistory', JSON.stringify(newHistory));
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
      setResponse({
        success: false,
        code: 'ERROR',
        message: errorMessage
      });
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setLoadingStage('');
    }
  };

  const handleDownloadResponse = () => {
    if (!response) return;
    const dataStr = JSON.stringify({
      messageType: selectedMessageType,
      timestamp: new Date().toISOString(),
      responseTime,
      response
    }, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ess-response-${selectedMessageType}-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const selectedTemplate = selectedMessageType ? MESSAGE_TEMPLATES[selectedMessageType] : null;

  if (!canTrigger) {
    return (
      <Box sx={{ p: 3, bgcolor: colors.bgPrimary, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper
          elevation={0}
          sx={{ p: 4, maxWidth: 480, textAlign: 'center', bgcolor: colors.bgSecondary, border: `1px solid ${colors.border}`, borderRadius: 2 }}
        >
          <Typography variant="h6" sx={{ color: colors.textPrimary, mb: 1 }}>Access restricted</Typography>
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            Your account does not have permission to trigger manual messages. Contact your tenant admin to request the "messages:trigger" permission.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: colors.bgPrimary, minHeight: '100vh' }}>
      {/* Header: Command Bar + Loan Context Badge */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2,
          bgcolor: colors.bgSecondary,
          border: `1px solid ${colors.border}`,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: colors.textSecondary }}>
          <Command size={16} />
          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>⌘K</Typography>
        </Box>
        
        <TextField
          id="command-search"
          fullWidth
          size="small"
          placeholder="Search messages or enter loan ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleCommandSearch()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} color={colors.textMuted} />
              </InputAdornment>
            ),
            sx: {
              bgcolor: colors.bgPrimary,
              color: colors.textPrimary,
              borderRadius: 1,
              '& fieldset': { borderColor: colors.border },
              '&:hover fieldset': { borderColor: isDark ? '#475569' : '#cbd5e1' },
              '& input': { color: colors.textPrimary }
            }
          }}
        />
        
        <Button
          variant="contained"
          onClick={handleCommandSearch}
          sx={{
            bgcolor: '#3B82F6',
            minWidth: 100,
            '&:hover': { bgcolor: '#2563EB' }
          }}
          startIcon={<Search size={16} />}
        >
          Search
        </Button>
        
        {selectedLoan && (
          <Chip
            icon={<CheckCircle size={14} color="#10B981" />}
            label={`Loan: ${selectedLoan.essApplicationNumber || selectedLoan._id}`}
            onDelete={clearLoan}
            sx={{
              bgcolor: '#064E3B',
              color: '#6EE7B7',
              fontFamily: 'monospace',
              '& .MuiChip-deleteIcon': { color: '#6EE7B7' }
            }}
          />
        )}
      </Paper>

      {/* Main Grid */}
      <Grid container spacing={3}>
        {/* Left Panel (30%) */}
        <Grid item xs={12} md={4} lg={3}>
          <Box sx={{ position: 'sticky', top: 20 }}>
            {/* Loan Lookup */}
            <Paper
              elevation={0}
              sx={{
                mb: 2,
                p: 2,
                bgcolor: colors.bgSecondary,
                border: `1px solid ${colors.border}`,
                borderRadius: 2
              }}
            >
              <LoanLookup
                onLoanSelected={handleLoanSelected}
                selectedLoan={selectedLoan}
                compact={true}
              />
            </Paper>

            {/* Loan Details Card */}
            {selectedLoan && (
              <Fade in>
                <Paper
                  elevation={0}
                  sx={{
                    mb: 2,
                    p: 2,
                    bgcolor: colors.bgSecondary,
                    border: '2px solid #10B981',
                    borderRadius: 2
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <FileText size={16} color="#10B981" />
                    <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                      LOAN DETAILS
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: colors.textSecondary }}>App#:</Typography>
                      <Typography variant="body2" sx={{ color: colors.textPrimary, fontFamily: 'monospace' }}>
                        {selectedLoan.essApplicationNumber || selectedLoan._id}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: colors.textSecondary }}>Loan#:</Typography>
                      <Typography variant="body2" sx={{ 
                        color: selectedLoan.essLoanNumberAlias ? '#10B981' : colors.textMuted, 
                        fontFamily: 'monospace',
                        fontStyle: selectedLoan.essLoanNumberAlias ? 'normal' : 'italic'
                      }}>
                        {selectedLoan.essLoanNumberAlias || 'Not assigned'}
                      </Typography>
                    </Box>
                    
                    {selectedLoan.clientData && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" sx={{ color: colors.textSecondary }}>Client:</Typography>
                        <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                          {`${selectedLoan.clientData.firstName || ''} ${selectedLoan.clientData.lastName || ''}`.trim()}
                        </Typography>
                      </Box>
                    )}
                    
                    {selectedLoan.requestedAmount && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" sx={{ color: colors.textSecondary }}>Amount:</Typography>
                        <Typography variant="body2" sx={{ color: '#10B981', fontWeight: 600 }}>
                          {selectedLoan.requestedAmount?.toLocaleString()}
                        </Typography>
                      </Box>
                    )}
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: colors.textSecondary }}>Status:</Typography>
                      <Chip
                        label={selectedLoan.status || 'Unknown'}
                        size="small"
                        sx={{
                          bgcolor: '#10B981',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.65rem',
                          height: 20
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Quick Actions based on loan status */}
                  {suggestedMessages.length > 0 && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors.border}` }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Zap size={14} color="#FBBF24" />
                        <Typography variant="caption" sx={{ color: '#FBBF24', fontWeight: 600 }}>
                          QUICK ACTIONS
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {suggestedMessages.slice(0, 3).map((msg) => (
                          <Button
                            key={msg.type}
                            size="small"
                            variant="outlined"
                            onClick={() => handleSuggestedClick(msg)}
                            fullWidth
                            sx={{
                              justifyContent: 'flex-start',
                              color: colors.textPrimary,
                              borderColor: colors.border,
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              py: 0.75,
                              '&:hover': {
                                borderColor: '#10B981',
                                bgcolor: '#10B98120'
                              }
                            }}
                            startIcon={<Send size={12} />}
                          >
                            {msg.displayName}
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Paper>
              </Fade>
            )}

            {/* Suggested Messages */}
            {suggestedMessages.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  mb: 2,
                  p: 2,
                  bgcolor: colors.bgSecondary,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 2
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Star size={16} color="#FBBF24" />
                  <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                    SUGGESTED
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {suggestedMessages.map((msg) => (
                    <Box
                      key={msg.type}
                      onClick={() => handleSuggestedClick(msg)}
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: selectedMessageType === msg.type ? colors.bgTertiary : 'transparent',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        '&:hover': { bgcolor: colors.bgTertiary }
                      }}
                    >
                      <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                        • {msg.displayName}
                      </Typography>
                      {msg.type === suggestedMessages[0]?.type && (
                        <Star size={12} color="#FBBF24" fill="#FBBF24" />
                      )}
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}

            {/* History */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: colors.bgSecondary,
                border: `1px solid ${colors.border}`,
                borderRadius: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Clock size={16} color={colors.textSecondary} />
                <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                  HISTORY
                </Typography>
              </Box>

              {messageHistory.length === 0 ? (
                <Typography variant="body2" sx={{ color: colors.textMuted, textAlign: 'center', py: 2 }}>
                  No messages sent yet
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                  {messageHistory.slice(0, 10).map((item) => (
                    <Box
                      key={item.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 0.75,
                        borderBottom: `1px solid ${colors.border}`,
                        '&:last-child': { borderBottom: 'none' }
                      }}
                    >
                      {item.status === 'success' ? (
                        <CheckCircle size={12} color="#10B981" />
                      ) : (
                        <XCircle size={12} color="#EF4444" />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.textPrimary,
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {item.displayName || item.type?.replace(/_/g, ' ')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.textMuted }}>
                          {new Date(item.timestamp).toLocaleTimeString()}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Box>
        </Grid>

        {/* Right Panel (70%) */}
        <Grid item xs={12} md={8} lg={9}>
          {/* Message Composer */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              bgcolor: colors.bgSecondary,
              border: `1px solid ${colors.border}`,
              borderRadius: 2
            }}
          >
            <Typography variant="h6" sx={{ color: colors.textPrimary, mb: 3, fontWeight: 600 }}>
              MESSAGE COMPOSER
            </Typography>

            {/* Message Type Selector */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel sx={{ color: colors.textSecondary }}>Select Message Type</InputLabel>
              <Select
                value={selectedMessageType}
                onChange={handleMessageTypeChange}
                label="Select Message Type"
                sx={{
                  bgcolor: colors.bgPrimary,
                  color: colors.textPrimary,
                  '& fieldset': { borderColor: colors.border },
                  '&:hover fieldset': { borderColor: isDark ? '#475569' : '#cbd5e1' },
                  '& .MuiSelect-icon': { color: colors.textSecondary }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: colors.bgSecondary,
                      color: colors.textPrimary,
                      maxHeight: 400
                    }
                  }
                }}
              >
                {filteredMessages.map((msg) => (
                  <MenuItem
                    key={msg.type}
                    value={msg.type}
                    disabled={!isMessageEnabled(msg.type)}
                    sx={{
                      '&:hover': { bgcolor: colors.bgTertiary },
                      '&.Mui-selected': { bgcolor: colors.bgTertiary },
                      opacity: isMessageEnabled(msg.type) ? 1 : 0.5
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: msg.categoryColor
                        }}
                      />
                      <Box>
                        <Typography variant="body2">
                          {msg.displayName}
                          {SENSITIVE_MESSAGE_TYPES.includes(msg.type) && !canTriggerSensitive && ' 🔒'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.textMuted }}>
                          {SENSITIVE_MESSAGE_TYPES.includes(msg.type) && !canTriggerSensitive
                            ? 'Requires elevated permission'
                            : msg.category}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedMessageType && (
              <>
                {/* Payload Editor Tabs */}
                <Box sx={{ mb: 2 }}>
                  <Tabs
                    value={payloadTab}
                    onChange={(e, v) => setPayloadTab(v)}
                    sx={{
                      '& .MuiTab-root': { color: colors.textSecondary },
                      '& .Mui-selected': { color: '#3B82F6' },
                      '& .MuiTabs-indicator': { bgcolor: '#3B82F6' }
                    }}
                  >
                    <Tab label="JSON" />
                    <Tab label="Visual" />
                  </Tabs>
                </Box>

                {/* Payload Editor - JSON Mode */}
                {payloadTab === 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ color: colors.textSecondary, mb: 1 }}>
                      PAYLOAD EDITOR
                    </Typography>
                    
                    <TextField
                      fullWidth
                      multiline
                      rows={12}
                      value={payload}
                      onChange={handlePayloadChange}
                      error={!!validationError}
                      helperText={validationError}
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          bgcolor: colors.bgPrimary,
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          color: colors.textPrimary,
                          '& fieldset': { borderColor: validationError ? '#EF4444' : colors.border },
                          '&:hover fieldset': { borderColor: validationError ? '#EF4444' : (isDark ? '#475569' : '#cbd5e1') }
                        },
                        '& .MuiFormHelperText-root': { color: '#EF4444' }
                      }}
                    />
                  </>
                )}

                {/* Payload Editor - Visual Mode */}
                {payloadTab === 1 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: colors.textSecondary, mb: 2 }}>
                      VISUAL EDITOR
                    </Typography>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: colors.bgPrimary,
                        border: `1px solid ${colors.border}`,
                        borderRadius: 2
                      }}
                    >
                      {(() => {
                        try {
                          const parsed = JSON.parse(payload);
                          return Object.entries(parsed).map(([key, value]) => (
                            <Box key={key} sx={{ mb: 2 }}>
                              <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600, textTransform: 'uppercase' }}>
                                {key.replace(/([A-Z])/g, ' $1').trim()}
                              </Typography>
                              <TextField
                                fullWidth
                                size="small"
                                multiline={key === 'MessageDetails' || (typeof value === 'string' && value.length > 80)}
                                rows={key === 'MessageDetails' ? 6 : (typeof value === 'string' && value.length > 80) ? 3 : 1}
                                value={typeof value === 'string' ? value : JSON.stringify(value)}
                                onChange={(e) => {
                                  try {
                                    const newPayload = { ...parsed };
                                    // Try to parse as JSON first, otherwise use as string
                                    try {
                                      newPayload[key] = JSON.parse(e.target.value);
                                    } catch {
                                      newPayload[key] = e.target.value;
                                    }
                                    setPayload(JSON.stringify(newPayload, null, 2));
                                  } catch (err) {
                                    console.error('Error updating field:', err);
                                  }
                                }}
                                sx={{
                                  mt: 0.5,
                                  '& .MuiOutlinedInput-root': {
                                    bgcolor: colors.bgSecondary,
                                    color: colors.textPrimary,
                                    fontFamily: key === 'MessageDetails' ? 'monospace' : 'inherit',
                                    fontSize: key === 'MessageDetails' ? '0.85rem' : 'inherit',
                                    '& fieldset': { borderColor: colors.border },
                                    '& textarea': { 
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word'
                                    }
                                  }
                                }}
                              />
                            </Box>
                          ));
                        } catch {
                          return (
                            <Typography variant="body2" sx={{ color: '#EF4444' }}>
                              Invalid JSON - switch to JSON tab to fix
                            </Typography>
                          );
                        }
                      })()}
                    </Paper>
                  </Box>
                )}

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={validateJSON}
                    sx={{
                      color: '#3B82F6',
                      borderColor: '#3B82F6',
                      '&:hover': { bgcolor: '#3B82F620', borderColor: '#3B82F6' }
                    }}
                  >
                    VALIDATE
                  </Button>
                  
                  <Button
                    variant="outlined"
                    startIcon={<RotateCcw size={16} />}
                    onClick={handleResetPayload}
                    sx={{
                      color: colors.textSecondary,
                      borderColor: colors.border,
                      '&:hover': { bgcolor: isDark ? '#47556920' : '#e2e8f020', borderColor: colors.border }
                    }}
                  >
                    Reset
                  </Button>
                  
                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Rocket size={16} />}
                    onClick={handleSend}
                    disabled={loading || !selectedMessageType}
                    sx={{
                      ml: 'auto',
                      bgcolor: '#8B5CF6',
                      '&:hover': { bgcolor: '#7C3AED' },
                      '&.Mui-disabled': { bgcolor: colors.bgTertiary }
                    }}
                  >
                    {loading ? loadingStage : 'SEND'}
                  </Button>
                </Box>

                <Divider sx={{ borderColor: colors.border, my: 3 }} />

                {/* Response Viewer */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <FileText size={16} color={isDark ? '#94A3B8' : '#64748b'} />
                  <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                    RESPONSE VIEWER
                  </Typography>
                  {response && (
                    <Button
                      size="small"
                      startIcon={<Download size={14} />}
                      onClick={handleDownloadResponse}
                      sx={{ ml: 'auto', color: colors.textSecondary }}
                    >
                      Download
                    </Button>
                  )}
                </Box>

                {!response ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      bgcolor: colors.bgPrimary,
                      border: `1px dashed ${colors.border}`,
                      borderRadius: 2,
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="body2" sx={{ color: colors.textMuted }}>
                      Response will appear here after sending
                    </Typography>
                  </Paper>
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: response.success ? (isDark ? '#064E3B' : '#d1fae5') : (isDark ? '#7F1D1D' : '#fee2e2'),
                      border: `1px solid ${response.success ? '#10B981' : '#EF4444'}`,
                      borderRadius: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      {response.success ? (
                        <CheckCircle size={24} color="#10B981" />
                      ) : (
                        <XCircle size={24} color="#EF4444" />
                      )}
                      <Box>
                        <Typography variant="h6" sx={{ color: colors.textPrimary }}>
                          {response.success ? 'Success!' : 'Failed'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: response.success ? '#10B981' : '#EF4444' }}>
                          Code: {response.code}
                          {responseTime && ` • ${responseTime}ms`}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {response.message && (
                      <Typography variant="body2" sx={{ color: colors.textPrimary, mb: 2 }}>
                        {response.message}
                      </Typography>
                    )}
                    
                    {/* Sent Message Section - Always show if sent exists */}
                    {response.data?.sent && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600, display: 'block', mb: 1 }}>
                          SENT MESSAGE (XML):
                        </Typography>
                        <Box
                          sx={{
                            p: 2,
                            bgcolor: colors.bgPrimary,
                            borderRadius: 1,
                            border: `1px solid ${colors.border}`,
                            maxHeight: 200,
                            overflowY: 'auto'
                          }}
                        >
                          <pre style={{
                            margin: 0,
                            fontFamily: 'monospace',
                            fontSize: '0.7rem',
                            color: isDark ? '#94A3B8' : '#64748b',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all'
                          }}>
                            {typeof response.data.sent === 'string' 
                              ? response.data.sent 
                              : JSON.stringify(response.data.sent, null, 2)}
                          </pre>
                        </Box>
                      </Box>
                    )}
                    
                    {/* ESS Response Section - Show if essResponse has content */}
                    {response.data?.essResponse && response.data.essResponse.trim() !== '' && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ color: '#10B981', fontWeight: 600, display: 'block', mb: 1 }}>
                          ESS RESPONSE (FROM UTUMISHI):
                        </Typography>
                        <Box
                          sx={{
                            p: 2,
                            bgcolor: isDark ? '#064E3B' : '#ecfdf5',
                            borderRadius: 1,
                            border: `1px solid #10B981`,
                            maxHeight: 200,
                            overflowY: 'auto'
                          }}
                        >
                          <pre style={{
                            margin: 0,
                            fontFamily: 'monospace',
                            fontSize: '0.7rem',
                            color: isDark ? '#10B981' : '#059669',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all'
                          }}>
                            {response.data.essResponse}
                          </pre>
                        </Box>
                      </Box>
                    )}
                    
                    {/* Show note if ESS Response is empty */}
                    {response.data?.sent && (!response.data?.essResponse || response.data.essResponse.trim() === '') && (
                      <Box sx={{ 
                        p: 1.5, 
                        bgcolor: isDark ? '#1E293B' : '#fef3c7', 
                        borderRadius: 1, 
                        border: `1px solid ${isDark ? '#FBBF24' : '#f59e0b'}` 
                      }}>
                        <Typography variant="caption" sx={{ color: isDark ? '#FBBF24' : '#b45309' }}>
                          ⚠️ ESS Response is empty - Message was sent but no response received from Utumishi yet.
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                )}
              </>
            )}

            {!selectedMessageType && (
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  bgcolor: colors.bgPrimary,
                  border: `1px dashed ${colors.border}`,
                  borderRadius: 2,
                  textAlign: 'center'
                }}
              >
                <Send size={32} color={isDark ? '#475569' : '#94a3b8'} style={{ marginBottom: 16 }} />
                <Typography variant="body1" sx={{ color: colors.textMuted }}>
                  Select a message type to begin
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                  Choose from the dropdown above or click a suggested message
                </Typography>
              </Paper>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ManualMessageTriggerV3;
